import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Hourglass, PartyPopper, X } from 'lucide-react'
import clsx from 'clsx'

import { useActivityBanner, useCountdown } from '@/shared/hooks/useActivityBanner'
import { Button } from '@/shared/components/ui/button'
import { appBtnClassName } from '@/shared/lib/app-button'

type CountdownParts = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

/** 单行倒计时 — 中文常见「27天14时42分05秒」紧凑格式 */
function CountdownInline({ countdown }: { countdown: CountdownParts }) {
  const { t } = useTranslation()

  const segments: { n: string; unit: string }[] = [
    ...(countdown.days > 0 ? [{ n: String(countdown.days), unit: t('home.announcement.days') }] : []),
    { n: String(countdown.hours).padStart(2, '0'), unit: t('home.announcement.hours') },
    { n: String(countdown.minutes).padStart(2, '0'), unit: t('home.announcement.minutes') },
    { n: String(countdown.seconds).padStart(2, '0'), unit: t('home.announcement.seconds') },
  ]

  return (
    <span
      className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md bg-black/30 px-2 py-0.5 text-[11px] leading-none ring-1 ring-white/10 sm:text-xs"
      aria-label={t('home.announcement.remaining', countdown)}
    >
      <Hourglass size={11} className="shrink-0 text-amber-400/90" aria-hidden />
      <span className="shrink-0 text-indigo-200/75">{t('home.announcement.label')}</span>
      <span className="min-w-0 truncate font-mono tabular-nums tracking-tight">
        {segments.map((seg) => (
          <span key={seg.unit}>
            <span className="font-semibold text-amber-100">{seg.n}</span>
            <span className="text-indigo-300/65">{seg.unit}</span>
          </span>
        ))}
      </span>
    </span>
  )
}

function BannerMessage({ message, className }: { message: string; className?: string }) {
  const parts = message.split('|').map((part) => part.trim()).filter(Boolean)

  return (
    <p className={clsx('min-w-0 text-[11px] leading-snug text-indigo-50/90 sm:text-xs', className)}>
      <PartyPopper size={13} className="mr-1 inline shrink-0 -translate-y-px text-amber-400" aria-hidden />
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && (
            <span className="mx-1.5 inline-flex items-center text-indigo-300/35" aria-hidden>
              ·
            </span>
          )}
        </span>
      ))}
    </p>
  )
}

/** 首页顶部活动公告条 — 文案来自后端,倒计时标签走 UI i18n */
export function ActivityBanner() {
  const { t, i18n } = useTranslation()
  const { banner, visible, dismiss } = useActivityBanner()
  const countdown = useCountdown(banner.show_countdown ? banner.ends_at : undefined)

  if (!visible) return null

  const isRtl = i18n.dir() === 'rtl'
  const showCountdown = banner.show_countdown && countdown && !countdown.expired
  const hasCta = Boolean(banner.cta_label && banner.cta_url)

  return (
    <div
      className="relative z-[60] shrink-0 overflow-hidden border-b border-indigo-500/25 bg-gradient-to-r from-[#0f1024] via-[#151530] to-[#121228] text-white shadow-[0_2px_16px_rgba(67,56,202,0.15)]"
      dir={isRtl ? 'rtl' : 'ltr'}
      role="region"
      aria-label={banner.message.slice(0, 40)}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_120%_at_10%_0%,rgba(99,102,241,0.18),transparent_55%)]"
        aria-hidden
      />

      <div className="portal-container portal-container--wide relative py-1.5 sm:py-2">
        {/* 桌面：单行；手机：倒计时 + 关闭 → 文案 + CTA */}
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {showCountdown && countdown && <CountdownInline countdown={countdown} />}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={dismiss}
              className="ml-auto shrink-0 text-indigo-200/50 hover:bg-white/5 hover:text-white sm:ml-0 sm:hidden"
              aria-label={t('common.close')}
            >
              <X size={15} />
            </Button>
          </div>

          <div
            className={clsx(
              'flex min-w-0 flex-1 items-center gap-2',
              showCountdown && 'sm:border-l sm:border-white/10 sm:pl-3',
            )}
          >
            <BannerMessage message={banner.message} className="flex-1 sm:line-clamp-1" />

            {hasCta && (
              <Link
                to={banner.cta_url}
                className={appBtnClassName({
                  variant: 'primary',
                  size: 'xs',
                  className:
                    'shrink-0 rounded-full px-3 py-0.5 text-[10px] font-semibold shadow-[0_2px_12px_rgba(245,158,11,0.28)] sm:px-3.5 sm:py-1 sm:text-[11px]',
                })}
              >
                {banner.cta_label}
              </Link>
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={dismiss}
              className="hidden shrink-0 text-indigo-200/50 hover:bg-white/5 hover:text-white sm:inline-flex"
              aria-label={t('common.close')}
            >
              <X size={15} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
